import { Injectable } from '@angular/core';
import { INTL_TEL_INPUT } from '../constants/widget-constants';

/**
 * GeoService
 *
 * Singleton cache for the geo lookup. The client's IP doesn't change within a
 * page lifecycle, so the same response can be reused by every phone-input
 * instance instead of re-fetching per component.
 *
 * - First caller triggers the fetch.
 * - Concurrent callers share the same in-flight promise (no duplicate HTTP).
 * - Later callers replay the cached result instantly (no HTTP).
 * - On error, the cache is cleared so the next caller can retry fresh.
 */
@Injectable({ providedIn: 'root' })
export class GeoService {
  private cached: { country?: string } | null = null;
  private inflight: Promise<{ country?: string }> | null = null;

  lookup(): Promise<{ country?: string }> {
    if (this.cached) {
      return Promise.resolve(this.cached);
    }
    if (this.inflight) {
      return this.inflight;
    }

    this.inflight = fetch(INTL_TEL_INPUT.IPLOOKUP_URL)
      .then(res => {
        if (!res.ok) {
          throw new Error(`Geo lookup failed: ${res.status}`);
        }
        return res.json();
      })
      .then((data: { country?: string }) => {
        this.cached = data ?? {};
        this.inflight = null;
        return this.cached;
      })
      .catch(err => {
        this.inflight = null;
        throw err;
      });

    return this.inflight;
  }
}
